import {
  err,
  ok,
  parseSponsorBlockBucket,
  sponsorBlockRequestUrl,
  type AbortLike,
  type Result,
  type SponsorBlockSettings,
  type SponsorSegment,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { readJsonBody } from './json';
import type { ApiProblem } from './problem';

/**
 * The SponsorBlock lookup — the k-anonymity form, always: the service is sent the first four hex
 * characters of SHA-256(videoId) and answers a ~150-video bucket, so which video is playing never
 * leaves the browser. Hashing happens here rather than in core because SHA-256 is a platform
 * capability (`crypto.subtle`), the same line that keeps `fetch` out of core.
 *
 * Through the shared transport on purpose: one retry policy and one error classification for every
 * request the app makes, so a SponsorBlock lookup fails in the same words a video request does.
 *
 * Two measured shapes (2026-08-01) the code below answers to:
 * - **"No segments" is a `404` with the plain-text body `Not Found`** — not a `200 []`. It is the
 *   normal answer for most videos, so it maps to an empty list, never to a problem.
 * - A `200` is the bucket array; the video simply absent from it is the same nothing.
 */
export async function fetchSponsorSegments(
  platform: Platform,
  settings: SponsorBlockSettings,
  videoId: string,
  // `AbortLike`, not `AbortSignal` — screen models call down to here (R13).
  signal?: AbortLike,
): Promise<Result<readonly SponsorSegment[], ApiProblem>> {
  const prefix = await hashPrefix(videoId);
  // No WebCrypto (an insecure origin) means no *private* lookup, and the answer to that is not a
  // louder lookup: the feature is silently absent, exactly like a storyboard that did not arrive.
  if (prefix === null) return ok([]);

  const url = sponsorBlockRequestUrl(settings, prefix);
  if (url === null) return ok([]);

  const response = await platform.transport.send({ method: 'GET', url, headers: {}, signal });
  if (!response.ok) {
    if (response.error.status === 404) return ok([]);
    return err({ kind: 'transport', error: response.error });
  }

  const body = readJsonBody(response.value.body);
  if (!body.ok) return err(body.error);
  return ok(parseSponsorBlockBucket(body.value, videoId));
}

/**
 * First 2 bytes of SHA-256(videoId), as 4 hex characters — the bucket address. Exported for
 * DeArrow (`api/dearrow.ts`), whose lookups are the same k-anonymity scheme on the same server
 * family.
 */
export async function hashPrefix(videoId: string): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle as SubtleCrypto | undefined;
  if (subtle === undefined) return null;
  try {
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(videoId));
    return [...new Uint8Array(digest).slice(0, 2)]
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}
